import { object } from '@calendly/form/validation'
import { isEqual } from 'lodash-es'

import { determineMembers, getDescriptionInitialValue, getDescriptionValidationSchema } from 'components/event_type_edit'
import { isEtLocked } from 'services/event_type_locking'

import {
  getBookingPageOptionsInitialValues,
  getBookingPageOptionsValidationSchema,
  getFormatBookingPageOptionsValues
} from '../components/booking_page_options'
import {
  formatConfirmationPageValues,
  getConfirmationPageInitialValues,
  getConfirmationPageValidationSchema
} from '../components/confirmation_page'
import { formatDescriptionValue, mapDescriptionValue } from '../components/description'
import { getDoubleBookingRulesInitialValues } from '../components/free_busy_rules'
import { formatHostListMemberships, getFormatHostListValues, getHostListInitialValues } from '../components/hosts'
import {
  getFormatInviteeFormValues,
  getInviteeFormInitialValues,
  getInviteeFormValidationSchema
} from '../components/invitee_form'
import {
  getFormatLimitsAndBuffersValues,
  getLimitsAndBuffersInitialValues,
  getLimitsAndBuffersValidationSchema,
} from '../components/limits_and_buffers'
import { getFormatLockingRulesValues, getSectionLockInitialValues } from '../components/locking_rule_switch'
import {
  formatNameAndColorValues,
  getNameAndColorInitialValues,
  getNameAndColorValidationSchema
} from '../components/name_and_color_fields'
import { getFormatPaymentValues, getPaymentInitialValues, getPaymentValidationSchema } from '../components/payment'
import { panelLockNames, PANELS } from '../constants'
import { eventCommunicationValuesToServerData, eventTypeCommunicationsInitialValues } from '../notifications/utils'

export const getMoreOptionsPanelKeys = ({ isMETParent, showPaymentsUpfront = false }) => {
  const panelKeys = [
    PANELS.description,
    PANELS.limitsAndBuffers
  ]

  if (!isMETParent) {
    panelKeys.push(PANELS.freeBusyRules)
  }

  panelKeys.push(
    PANELS.bookingPageOptions,
    PANELS.inviteeForm,
    PANELS.notifications,
    PANELS.confirmationPage
  )

  if (!showPaymentsUpfront) {
    panelKeys.splice(panelKeys.indexOf(PANELS.notifications), 0, PANELS.payment)
  }

  return panelKeys
}

export const getInitialValues = ({ eventType, defaultAvailabilityRules, showPaymentsUpfront = false }) => {
  const { isMETParent } = eventType

  const communicationInitialValues = isMETParent ? eventTypeCommunicationsInitialValues(eventType) : {}

  let doubleBookingRulesInitialValues = {}
  if (!isMETParent) {
    const members = Object.values(determineMembers(eventType, defaultAvailabilityRules.timezone))

    doubleBookingRulesInitialValues = {
      ...getHostListInitialValues({ eventType, isCreation: false }),
      ...getDoubleBookingRulesInitialValues(members)
    }
  }

  return {
    ...getNameAndColorInitialValues(eventType),
    ...getDescriptionInitialValue(eventType),
    ...getLimitsAndBuffersInitialValues(eventType),
    ...doubleBookingRulesInitialValues,
    ...getBookingPageOptionsInitialValues(eventType),
    ...getInviteeFormInitialValues(eventType),
    ...(!showPaymentsUpfront ? getPaymentInitialValues(eventType) : {}),
    ...communicationInitialValues,
    ...getConfirmationPageInitialValues(eventType),
    ...getSectionLockInitialValues(eventType)
  }
}

export const getEventTypeLocks = (eventType, { showPaymentsUpfront = false } = {}) => ({
  [PANELS.nameAndColor]: isEtLocked(eventType, panelLockNames.nameAndColor),
  [PANELS.description]: isEtLocked(eventType, panelLockNames.description),
  [PANELS.limitsAndBuffers]: isEtLocked(eventType, panelLockNames.limitsAndBuffers),
  [PANELS.freeBusyRules]: false,
  [PANELS.bookingPageOptions]: isEtLocked(eventType, panelLockNames.bookingPageOptions),
  [PANELS.inviteeForm]: isEtLocked(eventType, panelLockNames.inviteeForm),
  ...(!showPaymentsUpfront ? {
    [PANELS.payment]: isEtLocked(eventType, panelLockNames.payment)
  } : {}),
  [PANELS.notifications]: isEtLocked(eventType, panelLockNames.notifications),
  [PANELS.confirmationPage]: isEtLocked(eventType, panelLockNames.confirmationPage)
})

const changedMembershipsOnly = (initialValues) => (membership) => {
  const initialMembershipsAttributes = formatHostListMemberships(initialValues).event_type_memberships_attributes || []
  const initialMembership = initialMembershipsAttributes.find((initial) => initial.id === membership.id)

  return !isEqual(initialMembership, membership)
}

export const getFormatValuesToServerData = ({ eventType, showPaymentsUpfront = false }) => (
  values, panelKeys = [], initialValues = {}
) => {
  const { isMETParent } = eventType
  const locks = isMETParent ? {} : getEventTypeLocks(eventType, { showPaymentsUpfront })

  const formatHostListValues = getFormatHostListValues({ eventType, isCreation: false })
  const formatBookingPageOptionsValues = getFormatBookingPageOptionsValues(eventType)
  const formatInviteeFormValues = getFormatInviteeFormValues(eventType)
  const formatPaymentValues = getFormatPaymentValues(eventType)
  const formatLockingRulesValues = getFormatLockingRulesValues(eventType)
  const formatLimitsAndBuffersValues = getFormatLimitsAndBuffersValues(eventType)

  const hostListAttributes = formatHostListValues(values)

  const freeBusyRulesAttributes = (!isMETParent && panelKeys.includes('freeBusyRules')) ? {
    ...hostListAttributes,
    event_type_memberships_attributes: hostListAttributes.event_type_memberships_attributes
      .filter(changedMembershipsOnly(initialValues))
  } : {}

  return {
    ...(!locks[PANELS.nameAndColor] ? formatNameAndColorValues(values) : {}),
    ...(!locks[PANELS.description] ? formatDescriptionValue(values) : {}),
    ...(!locks[PANELS.limitsAndBuffers] ? formatLimitsAndBuffersValues(values) : {}),
    ...freeBusyRulesAttributes,
    ...(!locks[PANELS.bookingPageOptions] ? formatBookingPageOptionsValues(values) : {}),
    ...(!locks[PANELS.inviteeForm] ? formatInviteeFormValues(values) : {}),
    ...(!showPaymentsUpfront && !locks[PANELS.payment] ? formatPaymentValues(values) : {}),
    ...(!locks[PANELS.notifications] ? eventCommunicationValuesToServerData(values) : {}),
    ...(!locks[PANELS.confirmationPage] ? formatConfirmationPageValues(values) : {}),
    ...formatLockingRulesValues(values)
  }
}

export const getValidationSchema = (eventType, { showPaymentsUpfront = false } = {}) => {
  const locks = eventType.isMETParent ? {} : getEventTypeLocks(eventType, { showPaymentsUpfront })

  return object({})
    .concat(!locks[PANELS.nameAndColor] ? getNameAndColorValidationSchema() : object({}))
    .concat(!locks[PANELS.description] ? getDescriptionValidationSchema() : object({}))
    .concat(!locks[PANELS.limitsAndBuffers] ? getLimitsAndBuffersValidationSchema() : object({}))
    .concat(!locks[PANELS.bookingPageOptions] ? getBookingPageOptionsValidationSchema(eventType) : object({}))
    .concat(!locks[PANELS.inviteeForm] ? getInviteeFormValidationSchema() : object({}))
    .concat(!showPaymentsUpfront && !locks[PANELS.payment] ? getPaymentValidationSchema() : object({}))
    .concat(!locks[PANELS.confirmationPage] ? getConfirmationPageValidationSchema() : object({}))
}

const getPanelFieldNames = (schema) => Object.keys(schema.fields)

export const getPanelFieldsMapping = ({ eventType, showPaymentsUpfront = false }) => Object.entries({
  [PANELS.description]: getPanelFieldNames(getDescriptionValidationSchema()),
  [PANELS.limitsAndBuffers]: getPanelFieldNames(getLimitsAndBuffersValidationSchema()),
  [PANELS.bookingPageOptions]: getPanelFieldNames(getBookingPageOptionsValidationSchema(eventType)),
  [PANELS.inviteeForm]: getPanelFieldNames(getInviteeFormValidationSchema()),
  ...(!showPaymentsUpfront ? {
    [PANELS.payment]: getPanelFieldNames(getPaymentValidationSchema())
  } : {}),
  [PANELS.confirmationPage]: getPanelFieldNames(getConfirmationPageValidationSchema())
})

export const valuesMapper = (values) => ({
  ...values,
  ...mapDescriptionValue(values),
})
